// Frontend/src/pages/auth/RegisterPage.tsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useRequestOTP, useVerifyOTP } from "@/hooks/useInvitations";
import OTPInput from "@/components/invitations/OTPInput";
import logoImg from "@/assets/logo.png";
import api from "@/api/client";
import { useLanguage } from "@/contexts/LanguageContext";

const PRIMARY = "#1F3A93";
const ACCENT = "#27AE60";
const BG = "#F5F6FA";

type Step = "details" | "otp" | "success";

interface RegistrationData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  condoName: string;
  rif: string;
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState<Step>("details");
  const [formData, setFormData] = useState<RegistrationData>({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    condoName: "",
    rif: "",
  });
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationError, setRegistrationError] = useState<string | null>(null);

  const { mutate: requestOTP, isPending: requestingOTP } = useRequestOTP();
  const { mutate: verifyOTP, isPending: verifyingOTP } = useVerifyOTP();

  function hexToRgba(hex: string, alpha = 1) {
    const h = hex.replace("#", "");
    const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r},${g},${b},${alpha})`;
  }

  // Password validation
  const passwordRequirements = {
    minLength: formData.password.length >= 8,
    hasLetters: /[a-zA-Z]/.test(formData.password),
    hasNumbers: /\d/.test(formData.password),
    passwordsMatch: formData.password === formData.confirmPassword && formData.confirmPassword !== "",
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate form
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName || !formData.condoName) {
      setError(t("register.error.requiredFields"));
      return;
    }

    if (!isPasswordValid) {
      setError(t("register.error.passwordRequirements"));
      return;
    }

    // Request OTP for email verification
    requestOTP(
      { email: formData.email, purpose: "email_verify" },
      {
        onSuccess: () => {
          setStep("otp");
          setError(null);
        },
        onError: (err: any) => {
          const msg =
            err?.response?.data?.error ||
            err?.response?.data?.detail ||
            t("register.error.sendOtp");
          setError(msg);
        },
      }
    );
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (otp.length !== 6) {
      setError(t("register.error.enterOtp"));
      return;
    }

    // First verify OTP
    verifyOTP(
      { email: formData.email, code: otp, purpose: "email_verify" },
      {
        onSuccess: async () => {
          // OTP verified, now create the account
          try {
            await api.post("/auth/register/", {
              email: formData.email,
              password: formData.password,
              first_name: formData.firstName,
              last_name: formData.lastName,
              condo_name: formData.condoName,
              rif: formData.rif,
              role: "HOA_ADMIN",
            });
            
            setStep("success");
            setError(null);
          } catch (err: any) {
            const msg =
              err?.response?.data?.error ||
              err?.response?.data?.email?.[0] ||
              err?.response?.data?.detail ||
              t("register.error.createAccount");
            setRegistrationError(msg);
            setError(msg);
          }
        },
        onError: (err: any) => {
          const msg =
            err?.response?.data?.error ||
            err?.response?.data?.detail ||
            t("register.error.invalidOtp");
          setError(msg);
        },
      }
    );
  };

  const handleResendOTP = () => {
    setError(null);
    requestOTP(
      { email: formData.email, purpose: "email_verify" },
      {
        onSuccess: () => {
          setError(null);
        },
        onError: (err: any) => {
          const msg =
            err?.response?.data?.error ||
            err?.response?.data?.detail ||
            t("register.error.resendOtp");
          setError(msg);
        },
      }
    );
  };

  if (step === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: BG }}>
        <div className="w-full max-w-[440px] md:rounded-3xl md:border md:border-black/10 md:bg-white md:shadow-md p-6 md:p-10">
          <div className="flex flex-col items-center mb-6">
            <div
              className="h-16 w-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: hexToRgba(ACCENT, 0.15) }}
            >
              <CheckCircleIcon className="h-8 w-8" color={ACCENT} />
            </div>
            <h1 className="text-center text-2xl font-semibold mb-2 text-gray-900">
              {t("register.success.title")}
            </h1>
            <p className="text-center text-sm text-gray-600 mb-6">
              {t("register.success.subtitle")}
            </p>
          </div>

          <button
            onClick={() => navigate("/login")}
            className="w-full inline-flex items-center justify-center rounded-2xl px-4 py-3 text-[15px] font-semibold text-white transition-all"
            style={{ background: `linear-gradient(90deg, ${PRIMARY} 0%, ${PRIMARY} 70%, ${ACCENT} 100%)` }}
          >
            {t("register.success.continue")}
            <ArrowRightCircleIcon className="ml-2 h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  if (step === "otp") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: BG }}>
        <div className="w-full max-w-[440px] md:rounded-3xl md:border md:border-black/10 md:bg-white md:shadow-md p-6 md:p-10">
          <div className="flex flex-col items-center mb-6">
            <img src={logoImg} alt="Urbe360 logo" width={80} height={80} className="mb-2" />
            <span className="text-[18px] font-semibold text-gray-900">Urbe360</span>
          </div>

          <h1 className="text-center text-2xl font-semibold mb-2 text-gray-900">
            {t("register.otp.title")}
          </h1>
          <p className="text-center text-sm text-gray-600 mb-6">
            {t("register.otp.sentTo")} <strong>{formData.email}</strong>
          </p>

          <form onSubmit={handleOTPSubmit} className="space-y-5">
            <div className="space-y-2">
              <label className="block text-sm text-gray-800">{t("register.otp.enterCode")}</label>
              <OTPInput
                length={6}
                value={otp}
                onChange={setOtp}
                disabled={verifyingOTP}
                error={!!error}
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={verifyingOTP || otp.length !== 6}
              className="w-full inline-flex items-center justify-center rounded-2xl px-4 py-3 text-[15px] font-semibold text-white transition-all disabled:opacity-60"
              style={{ background: `linear-gradient(90deg, ${PRIMARY} 0%, ${PRIMARY} 70%, ${ACCENT} 100%)` }}
            >
              {verifyingOTP ? t("register.otp.verifying") : t("register.otp.verifyCreate")}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={requestingOTP}
                className="text-sm underline underline-offset-4"
                style={{ color: PRIMARY }}
              >
                {requestingOTP ? t("register.otp.sending") : t("register.otp.resend")}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setStep("details");
                  setOtp("");
                  setError(null);
                }}
                className="text-sm text-gray-600 underline underline-offset-4"
              >
                {t("register.otp.back")}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ backgroundColor: BG }}>
      <div className="w-full max-w-[520px] md:rounded-3xl md:border md:border-black/10 md:bg-white md:shadow-md p-6 md:p-10">
        <div className="flex flex-col items-center mb-6">
          <img src={logoImg} alt="Urbe360 logo" width={100} height={100} className="mb-2" />
          <span className="text-[18px] font-semibold text-gray-900">Urbe360</span>
        </div>

        <h1 className="text-center text-3xl font-semibold mb-2 text-gray-900">
          {t("register.details.title")}
        </h1>
        <p className="text-center text-sm text-gray-600 mb-6">
          {t("register.details.subtitle")}
        </p>

        <form onSubmit={handleDetailsSubmit} className="space-y-4">
          {/* Name fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="firstName" className="block text-sm text-gray-800">
                {t("register.firstName")} <span className="text-red-500">*</span>
              </label>
              <input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                disabled={requestingOTP}
                placeholder={t("register.firstNamePlaceholder")}
                className="w-full rounded-2xl bg-white text-gray-900 placeholder:text-gray-400 border px-4 py-3 outline-none transition"
                style={{ borderColor: "rgba(0,0,0,0.12)" }}
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="lastName" className="block text-sm text-gray-800">
                {t("register.lastName")} <span className="text-red-500">*</span>
              </label>
              <input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                disabled={requestingOTP}
                placeholder={t("register.lastNamePlaceholder")}
                className="w-full rounded-2xl bg-white text-gray-900 placeholder:text-gray-400 border px-4 py-3 outline-none transition"
                style={{ borderColor: "rgba(0,0,0,0.12)" }}
                required
              />
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm text-gray-800">
              {t("auth.email")} <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={requestingOTP}
              placeholder={t("auth.adminEmailPlaceholder")}
              className="w-full rounded-2xl bg-white text-gray-900 placeholder:text-gray-400 border px-4 py-3 outline-none transition"
              style={{ borderColor: "rgba(0,0,0,0.12)" }}
              required
            />
          </div>

          {/* Condo Name */}
          <div className="space-y-2">
            <label htmlFor="condoName" className="block text-sm text-gray-800">
              {t("register.condoName")} <span className="text-red-500">*</span>
            </label>
            <input
              id="condoName"
              type="text"
              value={formData.condoName}
              onChange={(e) => setFormData({ ...formData, condoName: e.target.value })}
              disabled={requestingOTP}
              placeholder={t("register.condoNamePlaceholder")}
              className="w-full rounded-2xl bg-white text-gray-900 placeholder:text-gray-400 border px-4 py-3 outline-none transition"
              style={{ borderColor: "rgba(0,0,0,0.12)" }}
              required
            />
          </div>

          {/* RIF */}
          <div className="space-y-2">
            <label htmlFor="rif" className="block text-sm text-gray-800">
              RIF <span className="text-gray-400 text-xs">(opcional)</span>
            </label>
            <input
              id="rif"
              type="text"
              value={formData.rif}
              onChange={(e) => setFormData({ ...formData, rif: e.target.value })}
              disabled={requestingOTP}
              placeholder="J-12345678-9"
              className="w-full rounded-2xl bg-white text-gray-900 placeholder:text-gray-400 border px-4 py-3 outline-none transition"
              style={{ borderColor: "rgba(0,0,0,0.12)" }}
            />
          </div>

          {/* Password */}
          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm text-gray-800">
              {t("auth.password")} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                disabled={requestingOTP}
                placeholder="••••••••"
                className="w-full rounded-2xl bg-white text-gray-900 placeholder:text-gray-400 border px-4 py-3 pr-12 outline-none transition"
                style={{ borderColor: "rgba(0,0,0,0.12)" }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <label htmlFor="confirmPassword" className="block text-sm text-gray-800">
              {t("auth.confirmPassword")} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                disabled={requestingOTP}
                placeholder="••••••••"
                className="w-full rounded-2xl bg-white text-gray-900 placeholder:text-gray-400 border px-4 py-3 pr-12 outline-none transition"
                style={{ borderColor: "rgba(0,0,0,0.12)" }}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          {formData.password && (
            <div className="rounded-2xl border px-4 py-3 bg-white" style={{ borderColor: "rgba(0,0,0,0.10)" }}>
              <p className="text-sm font-medium mb-2 text-gray-700">{t("passwordChange.requirementsTitle")}</p>
              <ul className="space-y-1 text-sm">
                <li className={passwordRequirements.minLength ? "text-green-600" : "text-gray-600"}>
                  {passwordRequirements.minLength ? "✓" : "○"} {t("passwordChange.requirementLength")}
                </li>
                <li className={passwordRequirements.hasLetters ? "text-green-600" : "text-gray-600"}>
                  {passwordRequirements.hasLetters ? "✓" : "○"} {t("passwordChange.requirementLetters")}
                </li>
                <li className={passwordRequirements.hasNumbers ? "text-green-600" : "text-gray-600"}>
                  {passwordRequirements.hasNumbers ? "✓" : "○"} {t("passwordChange.requirementNumbers")}
                </li>
                <li className={passwordRequirements.passwordsMatch ? "text-green-600" : "text-gray-600"}>
                  {passwordRequirements.passwordsMatch ? "✓" : "○"} {t("passwordChange.requirementMatch")}
                </li>
              </ul>
            </div>
          )}

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={requestingOTP || !isPasswordValid}
            className="w-full inline-flex items-center justify-center rounded-2xl px-4 py-3 text-[15px] font-semibold text-white transition-all disabled:opacity-60"
            style={{ background: `linear-gradient(90deg, ${PRIMARY} 0%, ${PRIMARY} 70%, ${ACCENT} 100%)` }}
          >
            {requestingOTP ? t("register.details.sendingCode") : t("auth.continue")}
            <ArrowRightCircleIcon className="ml-2 h-5 w-5" />
          </button>

          <div className="text-center text-sm text-gray-600">
            {t("auth.hasAccount")}{" "}
            <Link to="/login" className="underline underline-offset-4" style={{ color: PRIMARY }}>
              {t("auth.signIn")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

/* Icons */
function EyeIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function EyeSlashIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 3l18 18M2 12s3.5-7 10-7c2.4 0 4.4.8 6 2M22 12s-3.5 7-10 7c-2.4 0-4.4-.8-6-2"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowRightCircleIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" opacity="0.85" />
      <path d="M10 8l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckCircleIcon({ className = "", color }: { className?: string; color?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden style={color ? { color } : undefined}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
